import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { ExternalLink } from 'lucide-react';

import { BILLING_PATH, CopyAction, CopyButton, ErrorCallout } from '../components/ui';
import { ApiError, apiText, asApiError } from '../lib/api';
import { useDocumentTitle } from '../lib/hooks';
import { useApiOrigin } from '../lib/plane-config';
import { useBilling } from '../lib/session';

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
  /** An h3's h2, so the contents can show only the open section's subheadings. */
  section: string;
}

/** Methods in inline code like `POST /v1/tasks`, shown as a small coloured verb before the path. */
const ENDPOINT = /^(GET|POST|PUT|PATCH|DELETE) (\/\S*)$/;

function slug(text: string, used: Set<string>): string {
  const base = text.toLowerCase().replace(/[`'’"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
  let s = base;
  for (let i = 2; used.has(s); i++) s = `${base}-${i}`;
  used.add(s);
  return s;
}

const COPY_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
const CHECK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

/** The console's own note after the API reference: what a spending limit does. Billing links here. */
function SpendingLimits() {
  const { billing } = useBilling();
  const per = billing?.plan.usdPerHour ?? 0.78;
  const usd3 = (v: number) => `$${v.toFixed(3)}`;
  return (
    <section>
      <h2 id="spending-limits">Spending limits</h2>
      <p>
        A spending limit caps what your team’s Mac time can cost in a calendar month (UTC). Owners set it on <Link to={`${BILLING_PATH}#limit`}>Billing</Link>.
      </p>
      <ul>
        <li>
          <strong>Enforced.</strong> New work is refused with <code>402 budget-exceeded</code> when this month’s spend beyond your credit, plus every reservation in flight the
          credit won’t cover, plus the new work’s own timeout would pass the limit. Work already running is never stopped, so the limit is never exceeded.
        </li>
        <li>
          <strong>Not enforced.</strong> Nothing is refused. The limit is a line to watch: the bar on Billing turns amber at 80% and red past it.
        </li>
      </ul>
      <p>
        Each task and sandbox reserves its timeout’s worth of Mac time while it runs. A sandbox with a 1 hour timeout reserves {usd3(per)} when it opens; killed after 10 minutes,
        it costs {usd3(per / 6)} and the other {usd3((per * 5) / 6)} goes back at once. A limit caps your own money, so credit (the sign-up credit, or a deal such as the YC
        deal) never counts against it. Without a card, new work also has to fit in the credit that’s left.
      </p>
    </section>
  );
}

/** The three ways in, above the reference: an agent, GitHub Actions, the API. */
function StartHere() {
  const origin = useApiOrigin();
  const prompt = `Read ${origin}/SKILL.md and follow it to set up offstage cloud, so you can run builds, tests and commands on a real Mac.`;
  return (
    <section className="start" aria-labelledby="start-title">
      <h1 id="start-title">Docs</h1>
      <p className="start__lede">Three ways to put a Mac to work. The API reference follows.</p>
      <ol className="start__ways">
        <li className="start__way">
          <span className="start__num" aria-hidden="true">1</span>
          <h2>Set up with your agent</h2>
          <p>Paste one prompt into Claude Code, Codex or Cursor. It reads SKILL.md and asks you for a key once.</p>
          <div className="start__go">
            <CopyAction text={prompt} label="Copy prompt" doneLabel="Copied" size="sm" primary />
          </div>
        </li>
        <li className="start__way">
          <span className="start__num" aria-hidden="true">2</span>
          <h2>Run GitHub Actions</h2>
          <p>Connect your organization on the CI page, then change one line. Each job gets a fresh Mac.</p>
          <div className="start__line">
            <code>runs-on: offstage-macos</code>
            <CopyButton text="runs-on: offstage-macos" label="Copy the line" />
          </div>
          <div className="start__go">
            <Link className="btn btn--sm" to="/ci">
              Open CI
            </Link>
          </div>
        </li>
        <li className="start__way">
          <span className="start__num" aria-hidden="true">3</span>
          <h2>Call the API</h2>
          <p>JSON over HTTPS with a team key, or the TypeScript SDK on top of it.</p>
          <div className="start__line">
            <code>npm install offstage-cloud</code>
            <CopyButton text="npm install offstage-cloud" label="Copy the install command" />
          </div>
          <div className="start__go">
            <a className="btn btn--sm" href="#api-reference">
              API reference
            </a>
          </div>
        </li>
      </ol>
    </section>
  );
}

export function DocsPage() {
  useDocumentTitle('Docs');
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const load = () => {
    setError(null);
    apiText('/llms.txt').then(setMarkdown, (err) => setError(asApiError(err)));
  };
  useEffect(load, []);

  // The plane's own reference, rendered as is: it is first-party content from the same origin.
  const html = useMemo(() => (markdown === null ? '' : (marked.parse(markdown, { async: false, gfm: true }) as string)), [markdown]);

  useLayoutEffect(() => {
    const root = body.current;
    if (!root || !html) return;
    const used = new Set<string>();
    const found: Heading[] = [];
    /* The reference's own title becomes the section the start cards point at. */
    const title = root.querySelector('h1');
    if (title) {
      const h2 = document.createElement('h2');
      h2.textContent = 'API reference';
      h2.className = 'md__title';
      title.replaceWith(h2);
    }
    let section = '';
    root.querySelectorAll('h2, h3').forEach((el) => {
      const text = el.textContent ?? '';
      el.id = slug(text, used);
      if (el.tagName === 'H2') section = el.id;
      found.push({ id: el.id, text, level: el.tagName === 'H2' ? 2 : 3, section });
      const anchor = document.createElement('a');
      anchor.className = 'md__anchor';
      anchor.href = `#${el.id}`;
      anchor.setAttribute('aria-label', `Link to ${text}`);
      anchor.textContent = '#';
      el.appendChild(anchor);
    });
    root.querySelectorAll(':not(pre) > code').forEach((code) => {
      const m = ENDPOINT.exec(code.textContent ?? '');
      if (!m || code.classList.contains('endpoint')) return;
      const verb = document.createElement('span');
      verb.className = `endpoint__verb endpoint__verb--${m[1].toLowerCase()}`;
      verb.textContent = m[1];
      code.classList.add('endpoint');
      code.replaceChildren(verb, document.createTextNode(` ${m[2]}`));
    });
    /* Wide tables scroll on their own on a phone, not the page. */
    root.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('table-scroll')) return;
      const wrap = document.createElement('div');
      wrap.className = 'table-scroll';
      table.replaceWith(wrap);
      wrap.appendChild(table);
    });
    root.querySelectorAll('pre').forEach((pre) => {
      if (pre.parentElement?.classList.contains('codeblock')) return;
      // The button and the ramp lip sit on a wrapper, so they stay put while long lines scroll.
      const wrap = document.createElement('div');
      wrap.className = 'codeblock';
      pre.replaceWith(wrap);
      wrap.appendChild(pre);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'copy';
      button.setAttribute('aria-label', 'Copy code');
      button.innerHTML = COPY_ICON;
      button.addEventListener('click', () => {
        const text = pre.querySelector('code')?.textContent ?? pre.textContent ?? '';
        void navigator.clipboard?.writeText(text);
        button.innerHTML = CHECK_ICON;
        button.classList.add('is-done');
        window.setTimeout(() => {
          button.innerHTML = COPY_ICON;
          button.classList.remove('is-done');
        }, 1500);
      });
      wrap.appendChild(button);
    });
    root.querySelectorAll('a[href^="http"]').forEach((a) => {
      if ((a as HTMLAnchorElement).host !== window.location.host) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noreferrer');
      }
    });
    setHeadings(found);
  }, [html]);

  // Land on #section once the content exists.
  useEffect(() => {
    if (!headings.length || !location.hash) return;
    const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    el?.scrollIntoView({ block: 'start' });
    setActiveId(el?.id ?? null);
  }, [headings, location.hash]);

  useEffect(() => {
    if (!headings.length) return;
    const els = headings.map((h) => document.getElementById(h.id)).filter((e): e is HTMLElement => e !== null);
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '0px 0px -70% 0px', threshold: 0 },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [headings]);

  const open = headings.find((h) => h.id === activeId)?.section ?? headings[0]?.section;

  return (
    <div className="docs">
      <nav className="toc" aria-label="On this page">
        <p className="toc__label">On this page</p>
        {headings.length ? (
          <ol>
            {headings
              .filter((h) => h.level === 2 || h.section === open)
              .map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className={`${h.level === 3 ? 'is-sub' : ''}${activeId === h.id ? ' is-active' : ''}`}
                    aria-current={activeId === h.id ? 'location' : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      window.history.replaceState(null, '', `#${h.id}`);
                      setActiveId(h.id);
                    }}
                  >
                    {h.text}
                  </a>
                </li>
              ))}
          </ol>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {[80, 60, 70, 50].map((w) => (
              <span key={w} className="skeleton" style={{ width: `${w}%` }} />
            ))}
          </div>
        )}
        <div className="toc__more">
          <a href="/SKILL.md" target="_blank" rel="noreferrer">
            Agent skill, SKILL.md <ExternalLink width={12} height={12} aria-hidden="true" style={{ verticalAlign: -1 }} />
          </a>
          <a href="/llms.txt" target="_blank" rel="noreferrer">
            Raw llms.txt <ExternalLink width={12} height={12} aria-hidden="true" style={{ verticalAlign: -1 }} />
          </a>
        </div>
      </nav>
      <article className="md" aria-busy={markdown === null && !error}>
        {error ? <ErrorCallout error={error} title="The API reference didn’t load" onRetry={load} /> : null}
        {markdown === null && !error ? (
          <div className="stack">
            <span className="skeleton" style={{ height: 36, width: '50%' }} />
            {[95, 88, 92, 60, 85, 70].map((w, i) => (
              <span key={i} className="skeleton" style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : null}
        <StartHere />
        <div ref={body} className="md__body">
          <div dangerouslySetInnerHTML={{ __html: html }} />
          {html ? <SpendingLimits /> : null}
        </div>
      </article>
    </div>
  );
}
